import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Loader, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import 'iframe-resizer/js/iframeResizer.contentWindow';
import { config } from '../../../config';
import { getCustomLogger } from '../../lib/logger';
import { getToken } from '../../lib/localStorageHandler';
import AppStoreBadgeImage from './assets/appstore.svg';
import GooglePlayBadgeImage from './assets/googleplay.svg';

const logger = getCustomLogger('Microtools: common');

// Gemini model configuration
export const GEMINI_MODEL = 'gemini-2.5-flash-lite';

// Common interfaces
export interface UploadState {
  uploading: boolean;
  progress: number;
  uploadComplete: boolean;
  error: string | null;
}

export interface CopyState {
  copySuccess: boolean;
}

export interface MediaRecorderState extends UploadState {
  isRecording: boolean;
}

export interface MediaStreamRefs {
  mediaRecorder: React.MutableRefObject<MediaRecorder | null>;
  chunks: React.MutableRefObject<Blob[]>;
}

// Custom hooks
export const useIframeResizer = () => {
  useEffect(() => {
    // The iframe-resizer content window script auto-initializes
  }, []);
};

// Common utilities
export const finishProcessing = (url: string, openInNewTab?: boolean) => {
  // Get the part after the hash
  const hashPart = window.location.hash.split('?')[1] || '';
  const urlParams = new URLSearchParams(hashPart);
  const intent = urlParams.get('intent') || '';

  const redirectUrl = `${config.appBaseUrl}/#/dashboard?importUrl=${encodeURIComponent(
    url,
  )}${intent ? `&intent=${encodeURIComponent(intent)}` : ''}`;

  if (openInNewTab) {
    window.open(redirectUrl, '_blank');
  } else {
    window.parent.location.href = redirectUrl;
  }
};

// Common API functions
export const sanitizeFileName = (fileName: string): string => {
  return fileName
    .replace(/-/g, '_') // Replace hyphens with underscores
    .replace(/#/g, '') // Remove hash symbols
    .replace(/[^a-zA-Z0-9._\s]/g, '') // Remove any other special characters
    .trim(); // Remove leading/trailing whitespace
};

export const uploadFileToS3 = async (
  blob: Blob,
  uploadUrl: string,
  downloadUrl: string,
  onProgress: (progress: number) => void,
  onComplete: () => void,
  openInNewTab?: boolean,
  skipRedirect?: boolean,
) => {
  const xhr = new XMLHttpRequest();
  xhr.open('PUT', uploadUrl, true);
  xhr.setRequestHeader('Content-Type', blob.type);

  xhr.upload.onprogress = (event) => {
    if (event.lengthComputable) {
      const progress = (event.loaded / event.total) * 100;
      onProgress(progress);
    }
  };

  xhr.onload = () => {
    if (xhr.status === 200) {
      onComplete();
      if (!skipRedirect) {
        finishProcessing(downloadUrl, openInNewTab);
      }
    }
  };

  xhr.send(blob);
};

export const uploadToScreenApp = async (
  blob: Blob,
  fileName: string,
  onStart: () => void,
  onProgress: (progress: number) => void,
  onComplete: (downloadUrl?: string) => void,
  onError: (error: string) => void,
  openInNewTab?: boolean,
  skipRedirect?: boolean,
) => {
  onStart();

  try {
    const sanitizedFileName = sanitizeFileName(fileName);
    const response = await fetch(
      'https://api.screenapp.io/v2/temp-files/urls',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: sanitizedFileName }),
      },
    );

    const data = await response.json();
    if (data.success) {
      const { downloadUrl } = data.data;
      await uploadFileToS3(
        blob,
        data.data.uploadUrl,
        downloadUrl,
        onProgress,
        () => onComplete(downloadUrl),
        openInNewTab,
        skipRedirect,
      );
    } else {
      throw new Error(data.message || 'Failed to get download URL');
    }
  } catch (error) {
    logger.error('Error uploading to screen app', error);
    onError('Upload failed. Please try again.');
  }
};

// Media handling utilities
export const initializeMediaRecorder = (
  mediaRecorder: React.MutableRefObject<MediaRecorder | null>,
  chunks: React.MutableRefObject<Blob[]>,
  onDataAvailable: (event: BlobEvent) => void,
) => {
  const recorder = mediaRecorder.current;
  if (recorder) {
    recorder.ondataavailable = onDataAvailable;
    return Object.assign(chunks, { current: [] });
  }
  return chunks;
};

export const stopMediaRecording = (
  mediaRecorder: React.MutableRefObject<MediaRecorder | null>,
  streams: (MediaStream | null)[],
  animationFrame?: React.MutableRefObject<number | null>,
) => {
  if (mediaRecorder.current) {
    mediaRecorder.current.stop();

    // Stop all tracks
    streams.forEach((stream) => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    });

    // Cancel animation frame if it exists
    if (animationFrame?.current) {
      cancelAnimationFrame(animationFrame.current);
    }
  }
};

// Text to Speech API

/**
 * TTS Provider options
 */
export type TTSProvider = 'groq' | 'google';

/**
 * Configuration options for Text-to-Speech conversion
 */
export interface TTSOptions {
  provider?: TTSProvider;
  // Google TTS specific options
  prompt?: string; // Styling instructions for voice (Google only)
  model?: string;
  voice?: string;
  languageCode?: string;
  responseFormat?: string;
  speakingRate?: number; // 0.25 to 4.0
  pitch?: number; // -20.0 to 20.0
  volumeGainDb?: number; // -96.0 to 16.0
  // Groq TTS specific options
  speed?: number; // 0.25 to 4.0 (Groq only)
}

/**
 * Convert text to speech using the specified provider
 * Supports Groq (PlayAI) and Google Gemini TTS
 */
export const convertTextToSpeech = async (
  text: string,
  options: TTSOptions = {},
): Promise<Blob> => {
  const {
    provider = 'google', // Default to Google Gemini TTS
    prompt,
    model,
    voice,
    languageCode,
    responseFormat,
    speakingRate,
    pitch,
    volumeGainDb,
    speed,
  } = options;

  let url: string;
  let requestBody: Record<string, unknown>;

  if (provider === 'google') {
    // Google Gemini TTS (new default)
    url = `${config.authBaseUrlV2}/proxy/google/tts`;
    requestBody = {
      input: text,
      model: model || 'gemini-2.5-flash-tts',
      voice: voice || 'Charon',
      language_code: languageCode || 'en-US',
      response_format: responseFormat || 'mp3',
      speaking_rate: speakingRate || 1.0,
      pitch: pitch || 0.0,
      volume_gain_db: volumeGainDb || 0.0,
    };

    // Add prompt if provided (unique Google feature for voice styling)
    if (prompt) {
      requestBody.prompt = prompt;
    }
  } else {
    // Groq TTS (legacy)
    url = `${config.authBaseUrlV2}/proxy/groq/tts`;
    requestBody = {
      input: text,
      model: model || 'playai-tts',
      voice: voice || 'Arista-PlayAI',
      response_format: responseFormat || 'wav',
      speed: speed || 1.0,
    };
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(
      `Failed to convert text to speech (${provider}): ${errorText}`,
    );
  }

  return response.blob();
};

// Common UI Components
interface ProgressModalProps {
  open: boolean;
  progress: number;
  isComplete: boolean;
  processingMessage?: string;
  completeMessage?: string;
  onReset?: () => void;
  onSaveAgain?: () => void;
  onClose?: () => void;
}

export const ProgressModal: React.FC<ProgressModalProps> = ({
  open,
  progress,
  isComplete,
  processingMessage = 'Please wait while we process your file',
  completeMessage = 'Your recording is now ready to be processed.',
  onReset,
  onSaveAgain,
  onClose,
}) => {
  const { t: translate } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-6 text-center">
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-4 h-6 w-6"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        )}

        <p className="text-sm mb-6">
          {isComplete ? completeMessage : processingMessage}
        </p>

        <div className="w-full relative mb-8">
          <Progress value={progress} className="h-2" />
          <span className="absolute top-4 left-1/2 transform -translate-x-1/2 text-xs text-muted-foreground">
            {Math.round(progress)}%
          </span>
        </div>

        {!completeMessage?.includes('redirect') && (
          <DialogFooter className="pt-4 justify-center gap-2">
            {isComplete && onSaveAgain && (
              <Button onClick={onSaveAgain} size="sm">
                {translate('common.modal.saveAnother')}
              </Button>
            )}
            {isComplete && onReset && (
              <Button onClick={onReset} variant="outline" size="sm">
                {translate('common.modal.reset')}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

interface FeedbackSnackbarProps {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
  autoHideDuration?: number;
  onClose: () => void;
}

export const FeedbackSnackbar: React.FC<FeedbackSnackbarProps> = ({
  open,
  message,
  severity,
  autoHideDuration = 6000,
  onClose,
}) => {
  useEffect(() => {
    if (open && autoHideDuration > 0) {
      const timer = setTimeout(onClose, autoHideDuration);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open, autoHideDuration, onClose]);

  if (!open) return null;

  return (
    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 w-auto max-w-md">
      <Alert
        className={cn(
          'w-full',
          severity === 'error'
            ? 'border-destructive bg-destructive text-destructive-foreground'
            : 'border-green-500 bg-green-500 text-white',
        )}
      >
        <AlertDescription>{message}</AlertDescription>
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2 h-6 w-6 text-current hover:bg-white/20"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </Alert>
    </div>
  );
};

// Processing Indicator Component
export const ProcessingIndicator: React.FC<{ size?: number }> = ({
  size = 16,
}) => {
  return (
    <Loader className="animate-spin" style={{ width: size, height: size }} />
  );
};

export const callGeminiAPI = async (prompt: string): Promise<string> => {
  try {
    const response = await fetch(config.geminiProxyUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        contents: [
          {
            parts: [{ text: prompt }],
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`Gemini API error: ${response.statusText}`);
    }

    const data = await response.json();
    const result = data.candidates[0].content.parts[0].text;

    return result;
  } catch (error) {
    console.error('Error calling Gemini API:', error);
    throw error;
  }
};

export const callGroqForTTS = async (prompt: string): Promise<string> => {
  try {
    // Use the backend proxy endpoint for Groq API (OpenAI-compatible)
    const url = `${config.authBaseUrlV2}/proxy/groq/chat/completions`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        model: 'llama-3.1-8b-instant',
        messages: [
          {
            role: 'user',
            content: prompt,
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error(`Groq API error: ${response.statusText}`);
    }

    const data = await response.json();
    const result = data.choices[0].message.content;

    return result;
  } catch (error) {
    console.error('Error calling Groq API:', error);
    throw error;
  }
};

interface AppStoreBadgeProps {
  mobileUrl: string;
  showLabel?: boolean;
  height?: number;
}

export const AppStoreBadge: React.FC<AppStoreBadgeProps> = ({
  mobileUrl,
  showLabel = true,
  height = 48,
}) => {
  const { t } = useTranslation();

  const badgeStyle = {
    display: 'block',
    height: `${height}px`,
    width: 'auto',
    padding: 0,
    margin: 0,
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-1',
        showLabel ? 'h-auto' : `h-[${height}px]`,
      )}
    >
      {showLabel && (
        <span className="text-green-600 font-medium text-xs">
          {t('tools.mobileappcta.nowAvailable')}
        </span>
      )}
      <Button
        asChild
        variant="ghost"
        className="p-0 h-auto min-w-0 bg-transparent hover:bg-transparent rounded-none"
      >
        <a
          href={mobileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex justify-center items-center"
          style={{ height: `${height}px` }}
        >
          <img
            src={AppStoreBadgeImage}
            alt={t('tools.mobileappcta.appStoreAlt')}
            style={badgeStyle}
          />
        </a>
      </Button>
    </div>
  );
};

interface GooglePlayBadgeProps {
  mobileUrl: string;
  showLabel?: boolean;
  height?: number;
}

export const GooglePlayBadge: React.FC<GooglePlayBadgeProps> = ({
  mobileUrl,
  showLabel = true,
  height = 48,
}) => {
  const { t } = useTranslation();

  const badgeStyle = {
    display: 'block',
    height: `${height}px`,
    width: 'auto',
    padding: 0,
    margin: 0,
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-1',
        showLabel ? 'h-auto' : `h-[${height}px]`,
      )}
    >
      {showLabel && (
        <span className="text-green-600 font-medium text-xs">
          {t('tools.mobileappcta.nowAvailable')}
        </span>
      )}
      <Button
        asChild
        variant="ghost"
        className="p-0 h-auto min-w-0 bg-transparent hover:bg-transparent rounded-none"
      >
        <a
          href={mobileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex justify-center items-center"
          style={{ height: `${height}px` }}
        >
          <img
            src={GooglePlayBadgeImage}
            alt={t('tools.mobileappcta.googlePlayAlt')}
            style={badgeStyle}
          />
        </a>
      </Button>
    </div>
  );
};
